import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BackendService } from '../../../core/backend.service';
import { CONFIG } from '../../../core/config';
import { DropdownItem } from '../../../viewmodels/dropdown-item';
import { EquipmentClass } from '../../../viewmodels/equipment/equipment-class';
import { EquipmentClassType } from '../../../viewmodels/equipment/equipment-class-type';
import { EquipmentComponent } from '../../../viewmodels/equipment/equipment-component';
import { EquipmentItem } from '../../../viewmodels/equipment/equipment-item';
import { EquipmentPart } from '../../../viewmodels/equipment/equipment-part';
import { EquipmentSubUnit } from '../../../viewmodels/equipment/equipment-sub-unit';
import { EquipmentClassSelection, Model } from '../../../viewmodels/model';
import { DynamicFormControlService } from '../../dynamic-forms/dynamic-form-control.service';
import { AutoCompleteControl } from '../../dynamic-forms/form-models/autocomplete-control';
import { AutoCompleteDoubleControl } from '../../dynamic-forms/form-models/autocomplete-double-control';
import { FormControlBase } from '../../dynamic-forms/form-models/form-control-base';
import { PropertyPath } from '../../dynamic-forms/form-models/property-path';
import { FailureRateSelectionService } from '../../equipment-functions/failure-rate-selection.service';
import { EquipmentClassService } from './equipment-class.service';

export function equipmentClassCustomValidator(control: AutoCompleteDoubleControl) {
  return control.value === '' || control.value === '(none)' ? { invalidValue: { value: control.value } } : null;
}

@Component({
  selector: 'hb-equipment-class',
  templateUrl: './equipment-class.component.html',
  styleUrls: ['./equipment-class.component.scss'],
})
export class EquipmentClassComponent implements OnInit, OnDestroy {

  private noneDropdownLabel: string = this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.None');

  private _model: EquipmentClassSelection;

  get model(): EquipmentClassSelection {
    return this._model;
  }

  @Input() set model(value: EquipmentClassSelection) {
    const isChanged = this._model != null && value !== this._model;

    this._model = value;

    if (isChanged) {
      this.initializeControls();
    }
  }

  private _isEditable: boolean = true;

  get isEditable(): boolean {
    return this._isEditable;
  }

  @Input() set isEditable(value: boolean) {
    this._isEditable = value;
  }

  @Input() formGroup: FormGroup;
  @Input() allowUserEntry: boolean = false;

  @Output() equipmentClassTypeChanged = new EventEmitter<string>();
  @Output() selectionsChanged = new EventEmitter();

  public equipmentClassControl = new AutoCompleteDoubleControl({
    key: 'equipmentClassName',
    leftFormControlId: 'equipmentClassInput',
    rightFormControlId: 'equipmentClassNameInput',
    label: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.EquipmentClass'),
    propertyName: PropertyPath.get((m: Model) => m.equipmentClassItem),
    required: true,
    disabled: !this.isEditable,
    visible: true,
    leftKey: 'equipmentClass',
    leftPlaceholder: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.Code'),
    leftFieldName: 'identifier',
    rightFieldName: 'name',
    rightPlaceholder: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.Description'),
    valueFieldName: 'id',
    customValidators: [equipmentClassCustomValidator],
  });

  public equipmentTypeControl = new AutoCompleteDoubleControl({
    key: 'equipmentTypeName',
    leftFormControlId: 'equipmentTypeCodeInput',
    rightFormControlId: 'equipmentTypeNameInput',
    label: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.EquipmentType'),
    propertyName: PropertyPath.get((m: Model) => m.equipmentClassType),
    visible: true,
    disabled: true,
    leftKey: 'equipmentTypeCode',
    leftPlaceholder: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.Code'),
    leftFieldName: 'identifier',
    rightFieldName: 'name',
    rightPlaceholder: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.Description'),
    valueFieldName: 'id',
  });

  public equipmentSubUnitDropdown = new AutoCompleteControl({
    key: 'equipmentSubUnit',
    formControlId: 'equipmentSubUnitInput',
    label: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.SubUnit'),
    required: false,
    disabled: true,
    propertyName: PropertyPath.get((m: Model) => m.equipmentSubUnitItem.name),
    visible: true,
  });

  public equipmentComponentDropdown = new AutoCompleteControl({
    key: 'equipmentComponent',
    formControlId: 'equipmentComponentInput',
    label: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.Component'),
    required: false,
    disabled: true,
    propertyName: PropertyPath.get((m: Model) => m.equipmentComponentItem.name),
    visible: true,
  });

  public equipmentPartDropdown = new AutoCompleteControl({
    key: 'equipmentPart',
    formControlId: 'equipmentPartInput',
    label: this.i18NextPipe.transform('IsoHierarchy.EquipmentClass.Part'),
    required: false,
    disabled: true,
    propertyName: PropertyPath.get((m: Model) => m.equipmentPartItem.name),
    visible: true,
  });

  private controls: FormControlBase<any>[] = [
    this.equipmentClassControl,
    this.equipmentTypeControl,
    this.equipmentSubUnitDropdown,
    this.equipmentComponentDropdown,
    this.equipmentPartDropdown,
  ];

  private emptyEquipment: EquipmentItem;
  private unsubscribe: Subject<boolean> = new Subject();

  /**
   * Set when the component has been fully initialized in ngOnInit. Not used internally,
   * but checked externally.
   */
  isLoaded: boolean;

  constructor(
    private _backendService: BackendService,
    private _dynamicFormControlService: DynamicFormControlService,
    private _equipmentClassService: EquipmentClassService,
    private _failureRateSelectionService: FailureRateSelectionService,
    private i18NextPipe: I18NextPipe,
  ) { }

  ngOnInit() {
    if (!this.model || !this.formGroup) {
      return;
    }

    this.equipmentClassControl.allowUserEntry = this.allowUserEntry;
    this.equipmentTypeControl.allowUserEntry = this.allowUserEntry;
    this.equipmentSubUnitDropdown.allowUserEntry = this.allowUserEntry;
    this.equipmentComponentDropdown.allowUserEntry = this.allowUserEntry;
    this.equipmentPartDropdown.allowUserEntry = this.allowUserEntry;

    this.emptyEquipment = new EquipmentClass();
    this.emptyEquipment.id = CONFIG.constants.noneDropdownValue;
    this.emptyEquipment.name = this.noneDropdownLabel;
    this.emptyEquipment.identifier = this.noneDropdownLabel;

    this._dynamicFormControlService.addControls(this.formGroup, this.controls);
    this._dynamicFormControlService.setModel(this.model, this.controls);

    this.initializeEquipmentComboBoxes();

    this.validateControlHierarchy();

    this.isLoaded = true;

    this._failureRateSelectionService.invalidCriteriaSelected.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.resetControls();
    });

    this._failureRateSelectionService.selectionCriteriaChanged.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this._dynamicFormControlService.setModel(this.model, this.controls);
      this.initializeEquipmentComboBoxes();
      this.validateControlHierarchy();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  /*
   * Checks the value of a single control, then decides whether to enable or disable a subset of subsequent controls.
   */
  public validateNextControls(index: number): void {
    // Don't try to validate below the last control in the list
    if (index === this.controls.length - 1) {
      return;
    }

    const currentControl = this.controls[index];
    let currentControlInvalid = !currentControl.value || currentControl.value === CONFIG.constants.noneDropdownLabel;
    let nextControls = [];

    if (currentControl.key === 'equipmentTypeName' && !currentControl.disabled) {
      currentControlInvalid = false;
    }

    // If the current control is invalid, all subsequent controls should be disabled
    if (currentControlInvalid) {
      nextControls = this.controls.slice(index + 1, this.controls.length);
    } else {
      // Otherwise, we need to decide which controls to enable
      if (currentControl.key === 'equipmentClassName') {
        nextControls.push(this.controls[index + 2]);
      }
      nextControls.push(this.controls[index + 1]);
    }

    // Set disabled on all the controls we've decided to affect
    for (const control of nextControls) {
      control.disabled = currentControlInvalid;
    }
  }

  /**
   * Validates all controls in the list recursively.
   */
  private validateControlHierarchy(): void {
    if (!this.isEditable) {
      return;
    }

    for (let i = 0; i < this.controls.length; i++) {
      this.validateNextControls(i);
    }
  }

  /**
   * Called externally. Committing a control initializes its name, propertyName and value.
   */
  public commitControlValues() {
    this._dynamicFormControlService.commit(this.controls);
  }

  /**
   * These validate methods are consumed externally by the Model Details page to decide when to show a
   * warning message to the user.
   *
   * This logic should be moved to a validator.
   */
  public isClassControlValid(): boolean {
    const isClassEmpty = !this.equipmentClassControl.leftValue;
    const isNameEmpty = !this.equipmentClassControl.rightValue;

    if (isClassEmpty || isNameEmpty) {
      return false;
    }

    if (!this.equipmentClassControl.idValue) {
      if (
        this.equipmentClassControl.options.some(
          (it: EquipmentItem) =>
            this.compareStrings(it.identifier, this.equipmentClassControl.leftValue) ||
            this.compareStrings(it.name, this.equipmentClassControl.rightValue),
        )
      ) {
        return false;
      }
    }
    return true;
  }

  public validateEquipmentClassType() {
    const isCodeEmpty = !this.equipmentTypeControl.leftValue || this.equipmentTypeControl.leftValue === this.emptyEquipment.identifier;
    const isNameEmpty = !this.equipmentTypeControl.rightValue || this.equipmentTypeControl.rightValue === this.emptyEquipment.identifier;

    if ((!isCodeEmpty && isNameEmpty) || (isCodeEmpty && !isNameEmpty)) {
      this._equipmentClassService.equipmentClassTypeValidated.next(false);
      return;
    }

    if (isCodeEmpty && isNameEmpty) {
      this._equipmentClassService.equipmentClassTypeValidated.next(true);
      return;
    }
    if (!this.equipmentTypeControl.idValue) {
      this._backendService
        .getBooleanResponseForObject(CONFIG.apiRoutes.ValidateEquipmentClassType, {
          identifier: this.equipmentTypeControl.leftValue,
          Name: this.equipmentTypeControl.rightValue,
        })
        .pipe(take(1))
        .subscribe(valid => this._equipmentClassService.equipmentClassTypeValidated.next(valid));
      return;
    }
    this._equipmentClassService.equipmentClassTypeValidated.next(true);
  }

  private validateEquimentDropdown(control: AutoCompleteControl): boolean {
    if (!control.value || control.value === this.emptyEquipment.name) {
      return true;
    }

    if (control.options.some((it: DropdownItem) => this.compareStrings(it.label, control.value))) {
      return false;
    }

    return true;
  }

  public validateSubunitControl(): boolean {
    if (this.model.equipmentSubUnitId) {
      return true;
    }

    return this.validateEquimentDropdown(this.equipmentSubUnitDropdown);
  }

  public validateComponentControl(): boolean {
    if (this.model.equipmentComponentId) {
      return true;
    }

    return this.validateEquimentDropdown(this.equipmentComponentDropdown);
  }

  public validatePartControl(): boolean {
    if (this.model.equipmentPartId) {
      return true;
    }

    return this.validateEquimentDropdown(this.equipmentPartDropdown);
  }

  /**
   * Used to compare two strings, not taking into account surrounding whitespace or case differences.
   * @param {string} string1 The first string to compare.
   * @param {string} string2 The second string to compare.
   * @returns {boolean} True if the strings are equal, false otherwise.
   */
  private compareStrings(string1: string, string2: string): boolean {
    return string1.trim().toLowerCase() === string2.trim().toLowerCase();
  }

  /**
   * Called from ngOnInit. Used to set up subscriptions to the onChange events for each of the form controls,
   * so that they update the model when a new selection is made.
   *
   * I have to wonder if much of this could be replaced by simple Angular data binding. This seems like too inelegant a
   * solution for such a common use case. The dissonance between the data in the `model` object and what's shown in the
   * UI could cause serious user-visible bugs that would be difficult to track down if present.
   */
  private initializeEquipmentComboBoxes() {
    this.initializeHierarchy();

    /* Equipment Class */
    this.equipmentClassControl.onChange.pipe(takeUntil(this.unsubscribe)).subscribe((chosenItem: EquipmentItem) => {
      if (chosenItem.id && chosenItem.id === this.equipmentClassControl.idValue && chosenItem.id === this.model.equipmentClassId) {
        return;
      }
      this.model.equipmentClassId = chosenItem.id;
      this.model.equipmentClassItem = new EquipmentClass();
      this.model.equipmentClassItem.id = chosenItem.id;
      this.model.equipmentClassItem.identifier = chosenItem.identifier;
      this.model.equipmentClassItem.name = chosenItem.name;
      this.equipmentClassTypeChanged.emit(this.equipmentTypeControl.idValue);
      this.selectionsChanged.emit();

      if (chosenItem) {
        this.equipmentClassControl.value = chosenItem.name;
        this.equipmentClassControl.rightValue = chosenItem.name;
        this.equipmentClassControl.leftValue = chosenItem.identifier;
        this.loadEquipmentClassType(chosenItem.id, this.model.equipmentClassTypeId);
        if (this.equipmentClassControl.idValue) {
          this.loadEquipmentSubUnits(this.equipmentClassControl.idValue);
        }
      }

      // TODO:src eliminate magic numbers somehow
      this.validateNextControls(0);
    });

    /* Equipment Type */
    this.equipmentTypeControl.onChange.pipe(takeUntil(this.unsubscribe)).subscribe((chosenItem: EquipmentItem) => {
      this.model.equipmentClassTypeId = chosenItem.id;
      this.model.equipmentClassType = new EquipmentClassType();
      this.model.equipmentClassType.id = chosenItem.id;
      this.model.equipmentClassType.identifier = chosenItem.identifier;
      this.model.equipmentClassType.name = chosenItem.name;

      this.equipmentTypeControl.value = chosenItem.name;
      this.equipmentTypeControl.rightValue = chosenItem.name;
      this.equipmentTypeControl.leftValue = chosenItem.identifier;

      this.equipmentClassTypeChanged.emit(chosenItem.id);
      this.selectionsChanged.emit();

      this.validateNextControls(1);
    });

    /* Sub-Unit */
    this.equipmentSubUnitDropdown.onChange.subscribe((item: DropdownItem) => {
      this.model.equipmentSubUnitId = item.value;

      if (item.label === this.emptyEquipment.name) {
        this.model.equipmentSubUnitItem = null;
      } else {
        this.model.equipmentSubUnitItem = new EquipmentSubUnit();
        this.model.equipmentSubUnitItem.id = item.value ? item.value : undefined;
        this.model.equipmentSubUnitItem.name = item.label;
      }

      this.selectionsChanged.emit();

      if (item.value) {
        this.loadEquipmentComponents(item.value);
      }

      this.validateNextControls(2);
    });

    /* Component */
    this.equipmentComponentDropdown.onChange.subscribe((item: DropdownItem) => {
      this.model.equipmentComponentId = item.value;

      this.model.equipmentComponentItem = new EquipmentComponent();
      this.model.equipmentComponentItem.id = item.value ? item.value : undefined;
      this.model.equipmentComponentItem.name = item.label;

      this.selectionsChanged.emit();

      if (item.value) {
        this.loadEquipmentParts(item.value);
      }

      this.validateNextControls(3);
    });

    /* Part */
    this.equipmentPartDropdown.onChange.subscribe((item: DropdownItem) => {
      this.model.equipmentPartId = item.value;

      this.model.equipmentPartItem = new EquipmentPart();
      this.model.equipmentPartItem.id = item.value ? item.value : undefined;
      this.model.equipmentPartItem.name = item.label;
      this.model.equipmentPartItem.parentEquipmentComponentId = this.model.equipmentComponentItem.id;

      this.selectionsChanged.emit();
    });
  }

  private initializeHierarchy() {
    this.loadEquipmentClasses();

    if (this.model.equipmentClassId) {
      this.loadEquipmentSubUnits(this.model.equipmentClassId);
    } else {
      this.resetEquipmentSubUnit();
    }

    if (this.model.equipmentSubUnitId) {
      this.loadEquipmentComponents(this.model.equipmentSubUnitId);
    } else {
      this.resetEquipmentComponent();
    }

    if (this.model.equipmentComponentId) {
      this.loadEquipmentParts(this.model.equipmentComponentId);
    } else {
      this.resetEquipmentPart();
    }
  }

  private setEquipmentClass() {
    this.equipmentClassControl.disabled = !this.isEditable;
    this.setSelectedItemDoubleDropdown(this.equipmentClassControl, this.model.equipmentClassId);
    this.loadEquipmentClassType(this.model.equipmentClassId, this.model.equipmentClassTypeId);
  }

  private setSelectedItemDoubleDropdown(control: AutoCompleteDoubleControl, itemId: string) {
    const selectedItem = Array.from(control.options).find((x: EquipmentItem) => x.id === itemId) as EquipmentItem;

    if (itemId && selectedItem) {
      control.idValue = itemId;
      control.leftValue = selectedItem.identifier;
      control.rightValue = selectedItem.name;
      control.value = selectedItem.name;
    } else {
      control.leftValue = this.emptyEquipment.identifier;
      control.rightValue = this.emptyEquipment.name;
      control.value = this.emptyEquipment.name;
    }
    control.updateValues();
  }

  private populateDoubleDropdown(control: AutoCompleteDoubleControl, items: EquipmentItem[]) {
    if (!items) {
      control.options = [this.emptyEquipment];
      return;
    }

    control.options = [this.emptyEquipment].concat(items);
  }

  private populateEquipmentItemControl(control: AutoCompleteControl, items: EquipmentItem[]) {
    control.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];

    items.forEach(item => {
      control.options.push(new DropdownItem(item.name, item.id));
    });
  }

  private setSelectedItem(control: AutoCompleteControl, itemId: string) {
    const item = Array.from(control.options).find((opt: DropdownItem) => opt.value === itemId) as DropdownItem;
    if (item) {
      control.value = item.label;
      this.validateControlHierarchy();
    } else {
      control.value = this.emptyEquipment.name;
    }
  }

  private resetEquipmentSubUnit() {
    this.model.equipmentSubUnitId = CONFIG.constants.noneDropdownValue;
    this.model.equipmentSubUnitItem = new EquipmentSubUnit();
    this.model.equipmentSubUnitItem.id = undefined;
    this.model.equipmentSubUnitItem.name = this.noneDropdownLabel;
    this.equipmentSubUnitDropdown.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];
    this.equipmentSubUnitDropdown.value = this.noneDropdownLabel;
  }

  private resetEquipmentComponent() {
    this.model.equipmentComponentId = CONFIG.constants.noneDropdownValue;
    this.model.equipmentComponentItem = new EquipmentComponent();
    this.model.equipmentComponentItem.id = undefined;
    this.model.equipmentComponentItem.name = this.noneDropdownLabel;
    this.equipmentComponentDropdown.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];
    this.equipmentComponentDropdown.value = this.noneDropdownLabel;
  }

  private resetEquipmentPart() {
    this.model.equipmentPartId = CONFIG.constants.noneDropdownValue;
    this.model.equipmentPartItem = new EquipmentPart();
    this.model.equipmentPartItem.id = undefined;
    this.model.equipmentPartItem.name = this.noneDropdownLabel;
    this.equipmentPartDropdown.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];
    this.equipmentPartDropdown.value = this.noneDropdownLabel;
  }

  /**
   * Retreives the list of all Equipment Classes from the API for the user to choose from.
   */
  private loadEquipmentClasses() {
    this._backendService.getAll<EquipmentClass>(EquipmentClass).subscribe(equipmentClasses => {
      this.populateDoubleDropdown(this.equipmentClassControl, equipmentClasses);
      this.setEquipmentClass();
    });
  }

  /**
   * Retreives the list of all the Equipment Types belonging to the selected Equipment Class.
   * @param {string} classId The ID of the selected Equipment Class.
   * @param {string} typeId The previously selected Equipment Type. Used to restore the data if an Equipment Class is re-selected.
   */
  private loadEquipmentClassType(classId: string, typeId: string) {
    if (!classId) {
      this.equipmentTypeControl.options = [this.emptyEquipment];
      this.setSelectedItemDoubleDropdown(this.equipmentTypeControl, typeId);
      return;
    }

    this._backendService
      .getAllById<EquipmentClassType>(EquipmentClassType, classId, CONFIG.apiRoutes.equipmentClassType)
      .subscribe(equipmentClassTypes => {
        if (!equipmentClassTypes) {
          return;
        }

        this.populateDoubleDropdown(this.equipmentTypeControl, equipmentClassTypes);
        this.equipmentClassTypeChanged.emit(typeId);
        this.setSelectedItemDoubleDropdown(this.equipmentTypeControl, typeId);
        this.validateControlHierarchy();
      });
  }

  /**
   * Retreives the list of all the Sub-Units belonging to the selected Equipment Type.
   */
  private loadEquipmentSubUnits(equipmentClassId: string) {
    this._backendService
      .getAllById<EquipmentSubUnit>(EquipmentSubUnit, equipmentClassId, CONFIG.apiRoutes.equipmentSubUnit)
      .subscribe(equipmentSubUnits => {
        if (!equipmentSubUnits) {
          return;
        }
        this.populateEquipmentItemControl(this.equipmentSubUnitDropdown, equipmentSubUnits);
        this.setSelectedItem(this.equipmentSubUnitDropdown, this.model.equipmentSubUnitId);
      });
  }

  /**
   * Retreives the list of all the Components beloning to the selected Sub-Unit.
   */
  private loadEquipmentComponents(equipmentSubUnitId: string) {
    this._backendService
      .getAllById<EquipmentComponent>(EquipmentComponent, equipmentSubUnitId, CONFIG.apiRoutes.equipmentComponent)
      .subscribe(equipmentComponents => {
        if (!equipmentComponents) {
          return;
        }

        this.populateEquipmentItemControl(this.equipmentComponentDropdown, equipmentComponents);
        this.setSelectedItem(this.equipmentComponentDropdown, this.model.equipmentComponentId);
      });
  }

  /**
   * Retreives the list of all the Parts belonging to the selected Component.
   */
  private loadEquipmentParts(equipmentComponentId: string) {
    this._backendService.getAllById<EquipmentPart>(EquipmentPart, equipmentComponentId, CONFIG.apiRoutes.equipmentPart).subscribe(equipmentParts => {
      if (!equipmentParts) {
        return;
      }

      this.populateEquipmentItemControl(this.equipmentPartDropdown, equipmentParts);
      this.setSelectedItem(this.equipmentPartDropdown, this.model.equipmentPartId);
    });
  }

  /**
   * Sets the initial model of the component and calls for subscriptions to the FormControls' onChange events.
   */
  private initializeControls(): any {
    this._dynamicFormControlService.setModel(this.model, this.controls);
    this.initializeHierarchy();
  }

  private resetControls() {
    this.setSelectedItemDoubleDropdown(this.equipmentClassControl, '');
    this.setSelectedItemDoubleDropdown(this.equipmentTypeControl, '');
    this.equipmentSubUnitDropdown.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];
    this.equipmentSubUnitDropdown.value = this.noneDropdownLabel;
    this.equipmentComponentDropdown.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];
    this.equipmentComponentDropdown.value = this.noneDropdownLabel;
    this.equipmentPartDropdown.options = [new DropdownItem(this.emptyEquipment.name, this.emptyEquipment.id)];
    this.equipmentPartDropdown.value = this.noneDropdownLabel;
    this.validateControlHierarchy();
  }
}
